import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { getCellValue, getFilterText } from "./formatters";

/** "All rows on one page" sentinel for pageSize. */
export const ALL_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

/* Date-only comparison helpers for the date funnel. */
const dayOf = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
const isBlank = (v) => v == null || v === "";
/* Logical condition matcher for text / number columns. */
const condMatch = (raw, desc) => {
  const { op } = desc;
  if (op === "blank") return isBlank(raw);
  if (op === "notBlank") return !isBlank(raw);
  if (desc.valueType === "number") {
    const n = Number(raw); if (Number.isNaN(n)) return false;
    const a = Number(desc.val1); const b = Number(desc.val2);
    switch (op) {
      case "eq":  return n === a;
      case "ne":  return n !== a;
      case "gt":  return n >  a;
      case "gte": return n >= a;
      case "lt":  return n <  a;
      case "lte": return n <= a;
      case "between": return n >= a && n <= (Number.isNaN(b) ? Infinity : b);
      default: return true;
    }
  }
  const s = String(raw ?? "").toLowerCase();
  const v = String(desc.val1 ?? "").toLowerCase();
  if (!v) return true;
  switch (op) {
    case "contains":    return s.includes(v);
    case "notContains": return !s.includes(v);
    case "equals":      return s === v;
    case "notEquals":   return s !== v;
    case "beginsWith":  return s.startsWith(v);
    case "endsWith":    return s.endsWith(v);
    default: return true;
  }
};
const dateMatch = (raw, desc) => {
  if (desc.op === "blank") return isBlank(raw);
  if (desc.op === "notBlank") return !isBlank(raw);
  if (!desc?.op || !desc?.val1) return true;
  const rd = dayOf(raw);
  if (rd == null) return false;
  const d1 = dayOf(desc.val1);
  if (d1 == null) return true;
  switch (desc.op) {
    case "eq":  return rd === d1;
    case "gte": return rd >= d1;
    case "gt":  return rd >  d1;
    case "lte": return rd <= d1;
    case "lt":  return rd <  d1;
    case "between": {
      if (rd < d1) return false;
      if (desc.val2) { const d2 = dayOf(desc.val2); if (d2 != null && rd > d2) return false; }
      return true;
    }
    default: return true;
  }
};

/**
 * useGridState — owns sort, FUNNEL filters, search, pagination, column
 * visibility, column ORDER, column WIDTHS, density, and selection.
 *
 * Filters are descriptor objects, one per column key:
 *   { type: 'set',  set: Set<string> }      ← Excel-style checkbox list
 *   { type: 'date', op, val1, val2 }        ← date operators
 * Absence of a key = no filter on that column. An empty set = match nothing.
 * Filtering and the checkbox value-lists always run over the FULL dataset,
 * never the current page.
 *
 * All persistence (order / hidden / density / widths) is controlled-prop based;
 * omit the props for session-only behaviour.
 */
export const useGridState = ({
  data = [],
  columns,
  initialPageSize = 10,
  initialSort = null,
  initialFilters = {},
  initialHidden = [],
  initialDensity = "compact",
  serverMode = false,
  totalRowsOverride,
  getRowKey,
  onQueryChange,
  controlledSelected,
  onSelectionChange,

  controlledOrder,
  onColumnOrderChange,
  controlledHidden,
  onHiddenChange,
  controlledDensity,
  onDensityChange,
  controlledWidths,
  onColWidthsChange,
}) => {
  const [sort, setSort] = useState(initialSort);
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const colByKey = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns]);

  // ── Density ──
  const [internalDensity, setInternalDensity] = useState(controlledDensity ?? initialDensity);
  const density = controlledDensity ?? internalDensity;
  const setDensity = useCallback((d) => {
    if (onDensityChange) onDensityChange(d);
    if (controlledDensity == null) setInternalDensity(d);
  }, [onDensityChange, controlledDensity]);

  // ── Hidden columns ──
  const [internalHidden, setInternalHidden] = useState(new Set(initialHidden));
  const hiddenColumns = useMemo(
    () => (controlledHidden ? new Set(controlledHidden) : internalHidden),
    [controlledHidden, internalHidden]
  );
  const toggleColumn = useCallback((key) => {
    const next = new Set(hiddenColumns);
    next.has(key) ? next.delete(key) : next.add(key);
    if (onHiddenChange) onHiddenChange([...next]);
    if (controlledHidden == null) setInternalHidden(next);
  }, [hiddenColumns, onHiddenChange, controlledHidden]);

  // ── Column order ──
  const naturalKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const [internalOrder, setInternalOrder] = useState(null);
  const orderKeys = useMemo(() => {
    const stored = controlledOrder ?? internalOrder;
    if (!stored || !stored.length) return naturalKeys;
    const known = new Set(naturalKeys);
    const cleaned = stored.filter((k) => known.has(k));
    const missing = naturalKeys.filter((k) => !cleaned.includes(k));
    return [...cleaned, ...missing];
  }, [controlledOrder, internalOrder, naturalKeys]);

  const moveColumn = useCallback((fromKey, toKey) => {
    if (fromKey === toKey) return;
    const isPinned = (k) => colByKey[k]?.pinned === "left";
    const cur = orderKeys.slice();
    const fromIdx = cur.indexOf(fromKey);
    if (fromIdx === -1) return;
    cur.splice(fromIdx, 1);
    let insertAt = toKey == null ? cur.length : cur.indexOf(toKey);
    if (insertAt === -1) insertAt = cur.length;
    cur.splice(insertAt, 0, fromKey);
    const pinned = cur.filter(isPinned);
    const rest = cur.filter((k) => !isPinned(k));
    const next = [...pinned, ...rest];
    if (onColumnOrderChange) onColumnOrderChange(next);
    if (controlledOrder == null) setInternalOrder(next);
  }, [orderKeys, colByKey, onColumnOrderChange, controlledOrder]);

  // ── Column widths ──
  const [internalWidths, setInternalWidths] = useState({});
  const colWidths = controlledWidths ?? internalWidths;
  const setColWidth = useCallback((key, px) => {
    const next = { ...colWidths, [key]: Math.max(48, Math.round(px)) };
    if (onColWidthsChange) onColWidthsChange(next);
    if (controlledWidths == null) setInternalWidths(next);
  }, [colWidths, onColWidthsChange, controlledWidths]);

  const orderedColumns = useMemo(
    () => orderKeys.map((k) => colByKey[k]).filter(Boolean),
    [orderKeys, colByKey]
  );
  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !hiddenColumns.has(c.key)),
    [orderedColumns, hiddenColumns]
  );

  // ── Selection ──
  // NOTE: selection mutators are memoized, so they must read the LATEST
  // selection via a ref (not a captured closure) — otherwise a memoized
  // toggleRow keeps the first render's empty set and only ever selects one row.
  const [internalSelected, setInternalSelected] = useState(new Set());
  const selectedKeys = controlledSelected ?? internalSelected;
  const selectedRef = useRef(selectedKeys);
  selectedRef.current = selectedKeys;
  const commitSelection = useCallback((nextSet) => {
    if (onSelectionChange) onSelectionChange(nextSet);
    if (controlledSelected == null) setInternalSelected(nextSet);
  }, [onSelectionChange, controlledSelected]);
  const setSelectedKeys = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(selectedRef.current) : updater;
    commitSelection(next);
  }, [commitSelection]);

  // ── Unique value list for a column's set-filter (over ALL data) ──
  const uniqueValues = useCallback((key) => {
    const col = colByKey[key];
    if (!col) return [];
    const counts = new Map();
    data.forEach((r) => {
      const t = getFilterText(r, col);
      counts.set(t, (counts.get(t) || 0) + 1);
    });
    const isAmount = col.format === "number" || col.format === "currency";
    return [...counts.entries()].sort((a, b) =>
      isAmount
        ? (parseFloat(a[0]) || 0) - (parseFloat(b[0]) || 0)
        : String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true })
    );
  }, [data, colByKey]);

  // ── Filter mutators ──
  const clearColumnFilter = useCallback((key) => {
    setFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });
    setCurrentPage(1);
  }, []);

  // Commit any descriptor the popover produces:
  //   { type:'set',  set }            checkbox value list
  //   { type:'cond', op, val1, val2, valueType }   text/number condition
  //   { type:'date', op, val1, val2 } date condition
  const setColumnFilter = useCallback((key, descriptor) => {
    setFilters((prev) => {
      const n = { ...prev };
      if (!descriptor) delete n[key];
      else n[key] = descriptor;
      return n;
    });
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearch("");
    setCurrentPage(1);
  }, []);

  const columnFilter = useCallback((key) => filters[key], [filters]);
  const activeFilterCount = Object.keys(filters).length;

  // ── Client-side filter / sort ──
  const filtered = useMemo(() => {
    if (serverMode) return data;
    let rows = data;
    if (search) {
      const q = search.toLowerCase();
      const searchable = columns.filter((c) => c.searchable !== false);
      rows = rows.filter((r) =>
        searchable.some((c) => String(getCellValue(r, c) ?? "").toLowerCase().includes(q))
      );
    }
    Object.entries(filters).forEach(([key, desc]) => {
      const col = colByKey[key];
      if (!col || !desc) return;
      if (desc.type === "set") {
        rows = rows.filter((r) => desc.set.has(getFilterText(r, col)));
      } else if (desc.type === "cond") {
        rows = rows.filter((r) => condMatch(getCellValue(r, col), desc));
      } else if (desc.type === "date") {
        rows = rows.filter((r) => dateMatch(getCellValue(r, col), desc));
      }
    });
    return rows;
  }, [data, search, filters, columns, colByKey, serverMode]);

  const sorted = useMemo(() => {
    if (serverMode || !sort) return filtered;
    const col = colByKey[sort.key];
    if (!col) return filtered;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getCellValue(a, col);
      const bv = getCellValue(b, col);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      if (av instanceof Date && bv instanceof Date) return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [filtered, sort, colByKey, serverMode]);

  // ── Pagination (ALL_PAGE_SIZE collapses to a single page) ──
  const effectiveTotal = serverMode ? totalRowsOverride ?? data.length : sorted.length;
  const showingAll = !pageSize || pageSize >= ALL_PAGE_SIZE;
  const totalPages = showingAll ? 1 : Math.max(1, Math.ceil(effectiveTotal / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginated = useMemo(() => {
    if (serverMode) return data;
    if (showingAll) return sorted;
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize, serverMode, data, showingAll]);

  useEffect(() => {
    if (serverMode && onQueryChange) {
      onQueryChange({ sort, filters, search, page: currentPage, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, filters, search, currentPage, pageSize, serverMode]);

  // ── Actions ──
  const toggleSort = useCallback((key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  }, []);

  const isSelected = useCallback((row) => selectedKeys.has(getRowKey(row)), [selectedKeys, getRowKey]);
  const toggleRow = useCallback((row) => {
    const k = getRowKey(row);
    const n = new Set(selectedRef.current);
    n.has(k) ? n.delete(k) : n.add(k);
    commitSelection(n);
  }, [getRowKey, commitSelection]);
  const selectAllFiltered = useCallback((isRowSelectable = () => true) => {
    const n = new Set(selectedRef.current);
    sorted.forEach((r) => { if (isRowSelectable(r)) n.add(getRowKey(r)); });
    commitSelection(n);
  }, [sorted, getRowKey, commitSelection]);
  const deselectAllFiltered = useCallback(() => {
    const n = new Set(selectedRef.current);
    sorted.forEach((r) => n.delete(getRowKey(r)));
    commitSelection(n);
  }, [sorted, getRowKey, commitSelection]);
  const clearSelection = useCallback(() => commitSelection(new Set()), [commitSelection]);
  const allPageSelected = paginated.length > 0 && paginated.every((r) => selectedKeys.has(getRowKey(r)));

  return {
    rows: paginated,
    filteredRows: sorted,
    totalRows: effectiveTotal,
    showingAll,
    visibleColumns,
    orderedColumns,
    orderKeys,
    moveColumn,
    hiddenColumns,
    toggleColumn,
    colWidths,
    setColWidth,
    sort,
    toggleSort,
    setSort,
    // funnel filters
    filters,
    columnFilter,
    uniqueValues,
    setColumnFilter,
    clearColumnFilter,
    clearFilters,
    activeFilterCount,
    search,
    setSearch,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    selectedKeys,
    isSelected,
    toggleRow,
    selectAllFiltered,
    deselectAllFiltered,
    clearSelection,
    allPageSelected,
    density,
    setDensity,
  };
};
